import { useState, useEffect, Fragment } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { projectsApi } from '@/api/projects'
import { sectionsApi, phasesApi } from '@/api/sections'
import type { AxiosError } from 'axios'
import { auditApi, type AuditEntry } from '@/api/audit'
import { useAuthStore, useEffectiveRole } from '@/store/auth'
import StatusBadge from '@/components/projects/StatusBadge'
import AddSectionModal from '@/components/projects/AddSectionModal'
import EditSectionModal from '@/components/projects/EditSectionModal'
import EditProjectModal from '@/components/projects/EditProjectModal'
import TransferGipModal from '@/components/projects/TransferGipModal'
import LifecycleBadge from '@/components/projects/LifecycleBadge'
import GanttChart from '@/components/projects/GanttChart'
import Spinner from '@/components/common/Spinner'
import { format, differenceInCalendarDays } from 'date-fns'
import { ru } from 'date-fns/locale'
import { Archive, ArchiveRestore, ArrowLeft, Plus, Pencil, CheckCircle2, UserCheck, Trash2, StickyNote } from 'lucide-react'
import type { SectionOut, SectionStatus, ProjectColor, ProjectOut, DocType, DocPhaseOut } from '@/types'
import { DOC_TYPE_LABELS, DOC_TYPES } from '@/types'
import HelpButton from '@/components/common/HelpButton'

// ─── Словари статусов разделов ───────────────────────────────────────────────
const STATUS_LABELS: Record<SectionStatus, string> = {
  not_started: 'Не начат',
  in_progress:  'В работе',
  on_review:    'На проверке',
  suspended:    'Приостановлен',
  issued:       'Выдан',
  accepted:     'Принят',
}

/**
 * Переходы с пометкой направления (для отображения в select).
 * backward = возврат на предыдущий этап.
 */
interface Transition { to: SectionStatus; label: string; backward?: boolean }
const SECTION_TRANSITIONS: Record<SectionStatus, Transition[]> = {
  not_started: [
    { to: 'in_progress', label: '→ Начать работу' },
  ],
  in_progress: [
    { to: 'on_review', label: '→ Отправить на проверку' },
    { to: 'suspended', label: '→ Приостановить' },
  ],
  suspended: [
    { to: 'in_progress', label: '→ Возобновить' },
  ],
  on_review: [
    { to: 'issued', label: '→ Выдать' },
    { to: 'in_progress', label: '← Вернуть в работу', backward: true },
  ],
  issued: [
    { to: 'accepted', label: '→ Принять' },
    { to: 'on_review', label: '← Вернуть на проверку', backward: true },
  ],
  accepted: [
    { to: 'in_progress', label: '← Вернуть в доработку', backward: true },
  ],
}

// ─── Цветовые классы для прогресса ───────────────────────────────────────────
function progressColor(pct: number): string {
  if (pct >= 100) return 'bg-emerald-500'
  if (pct >= 60)  return 'bg-blue-500'
  if (pct >= 30)  return 'bg-amber-500'
  return 'bg-gray-300'
}

type Tab = DocType | 'history'

export default function ProjectCardPage() {
  const { id }   = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc       = useQueryClient()
  const user          = useAuthStore((s) => s.user)
  const effectiveRole = useEffectiveRole()

  const [showAddSection,   setShowAddSection]   = useState(false)
  const [editSection,      setEditSection]       = useState<SectionOut | null>(null)
  const [showEditProject,  setShowEditProject]   = useState(false)
  const [showTransferGip,  setShowTransferGip]   = useState(false)
  const [confirmComplete,  setConfirmComplete]   = useState(false)
  const [confirmDeleteSection, setConfirmDeleteSection] = useState<SectionOut | null>(null)
  const [confirmDeleteProject, setConfirmDeleteProject] = useState(false)
  const [activeTab,        setActiveTab]         = useState<Tab>('ird')
  const [stageCostWarning, setStageCostWarning]  = useState<string | null>(null)

  // ── Запросы данных ──────────────────────────────────────────────────────────
  // refetchInterval: чужие правки сроков подтягиваются фоном без перезагрузки страницы
  const { data: project, isLoading: pLoading } = useQuery({
    queryKey: ['project', id],
    queryFn:  () => projectsApi.get(id!).then((r) => r.data),
    enabled:  !!id,
    refetchInterval: 20_000,
  })

  const { data: phases = [] } = useQuery({
    queryKey: ['phases', id],
    queryFn:  () => phasesApi.list(id!).then((r) => r.data),
    enabled:  !!id,
    refetchInterval: 20_000,
  })

  const activeDocType = activeTab !== 'history' ? activeTab as DocType : undefined

  const { data: sections = [], isLoading: sLoading } = useQuery({
    queryKey: ['sections', id, activeDocType],
    queryFn:  () => sectionsApi.list(id!, activeDocType).then((r) => r.data),
    enabled:  !!id && activeTab !== 'history',
    refetchInterval: 20_000,
  })

  // Все разделы — только для расчёта статистики (не зависит от активной вкладки)
  const { data: allSections = [] } = useQuery({
    queryKey: ['sections', id, 'all'],
    queryFn:  () => sectionsApi.list(id!).then((r) => r.data),
    enabled:  !!id,
    refetchInterval: 20_000,
  })

  const { data: history = [], isLoading: hLoading } = useQuery({
    queryKey: ['audit', id],
    queryFn:  () => auditApi.getProjectHistory(id!).then((r) => r.data),
    enabled:  !!id && activeTab === 'history' && (effectiveRole === 'director' || effectiveRole === 'deputy_director'),
  })

  // ── Мутации ─────────────────────────────────────────────────────────────────
  const archiveMutation = useMutation({
    mutationFn: () => projectsApi.archive(id!),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['projects'] }); navigate('/projects') },
  })

  const unarchiveMutation = useMutation({
    mutationFn: () => projectsApi.unarchive(id!),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: ['project', id] })
      qc.invalidateQueries({ queryKey: ['projects'] })
    },
  })

  const deleteProjectMutation = useMutation({
    mutationFn: () => projectsApi.deleteProject(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] })
      navigate('/projects')
    },
  })

  // Примечание проекта — отдельная мутация для инлайн-правки в шапке карточки
  const noteMutation = useMutation({
    mutationFn: (note: string) => projectsApi.patch(id!, { note }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project', id] })
      qc.invalidateQueries({ queryKey: ['projects'] })
    },
  })

  const completeMutation = useMutation({
    mutationFn: () => projectsApi.complete(id!),
    onSuccess:  () => {
      qc.invalidateQueries({ queryKey: ['project', id] })
      qc.invalidateQueries({ queryKey: ['projects'] })
      setConfirmComplete(false)
    },
  })

  const statusMutation = useMutation({
    mutationFn: ({ sid, status }: { sid: string; status: SectionStatus }) =>
      sectionsApi.changeStatus(sid, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sections', id] })
      qc.invalidateQueries({ queryKey: ['audit', id] })
      qc.invalidateQueries({ queryKey: ['gantt'] })
      qc.invalidateQueries({ queryKey: ['dashboard-zones'] })
    },
  })

  const deleteSectionMutation = useMutation({
    mutationFn: (sectionId: string) => sectionsApi.delete(sectionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sections', id] })
      qc.invalidateQueries({ queryKey: ['audit', id] })
      qc.invalidateQueries({ queryKey: ['gantt'] })
      setConfirmDeleteSection(null)
    },
    onError: (_err: AxiosError) => {
      setConfirmDeleteSection(null)
    },
  })

  // ── Загрузка ────────────────────────────────────────────────────────────────
  if (pLoading) return <Spinner size="lg" />
  if (!project) return <div className="p-6 text-rose-600">Проект не найден</div>

  // ── Права доступа ───────────────────────────────────────────────────────────
  const isDirectorFamily = effectiveRole === 'director' || effectiveRole === 'deputy_director'
  // В режиме предпросмотра как ГИП — проверяем только роль (директор видит всё)
  const isProjectGip =
    effectiveRole === 'chief_engineer' &&
    (user?.role === 'director' /* предпросмотр */ ||
      project.chief_engineer_id === user?.id ||
      project.substitute_gip_id === user?.id)
  const canManage   = isDirectorFamily || isProjectGip
  const canEdit     = canManage && !project.is_archived
  const canComplete = isDirectorFamily && !project.is_archived &&
                      project.lifecycle_stage !== 'post_design'
  const canTransfer = isDirectorFamily && !project.is_archived

  // ── Цвет проекта — берём из бекенда (view project_color_status),
  //    чтобы не расходиться с цветом в списке проектов. ─────────────────────────
  const today = new Date()
  const color: ProjectColor =
    project.is_archived || project.lifecycle_stage === 'post_design'
      ? 'grey'
      : project.color ?? 'grey'

  // ── Статистика разделов ─────────────────────────────────────────────────────
  // Статистика считается по ВСЕМ разделам проекта, не только активной вкладки
  const sStats = {
    total:   allSections.length,
    done:    allSections.filter((s) => s.status === 'accepted').length,
    overdue: allSections.filter(
      (s) => new Date(s.planned_end) < today && s.status !== 'accepted' && s.status !== 'suspended',
    ).length,
  }

  // Готовность = среднее по прогрессу всех разделов
  // (manual_progress если задан, иначе по статусу: accepted=100, issued=90, on_review=70, in_progress=30, ост.=0)
  function sectionReadiness(s: SectionOut): number {
    if (s.status === 'accepted') return 100
    if (s.status === 'suspended') return 0
    if (s.manual_progress !== null && s.manual_progress !== undefined) return s.manual_progress
    switch (s.status) {
      case 'issued':     return 90
      case 'on_review':  return 70
      case 'in_progress': return 30
      default: return 0
    }
  }
  const overallProgress = sStats.total > 0
    ? Math.round(allSections.reduce((acc, s) => acc + sectionReadiness(s), 0) / sStats.total)
    : 0

  // ─── Рендер ─────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="p-6 space-y-6">

        {/* ── Шапка проекта ── */}
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <button
              onClick={() => navigate(-1)}
              className="mb-2 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"
            >
              <ArrowLeft className="h-4 w-4" /> Назад
            </button>
            <h1 className="text-xl font-semibold text-gray-900">
              {project.code} - {project.name}
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
              <span>{project.customer}</span>
              <span>·</span>
              <span>
                {format(new Date(project.start_date ?? project.created_at), 'dd.MM.yyyy')}
                {' - '}
                {format(new Date(project.planned_end_date), 'dd.MM.yyyy')}
              </span>
              {/* Этап проекта — единый источник из lifecycle_stage */}
              <LifecycleBadge stage={project.lifecycle_stage} />
              {/* Цветовой статус — показываем только для активных (не post_design) проектов */}
              {project.lifecycle_stage !== 'post_design' && (
                <>
                  <StatusBadge color={color} />
                  {project.manual_color && (
                    <span title="Статус задан вручную" className="text-xs text-gray-400 cursor-default">✋ вручную</span>
                  )}
                </>
              )}
              {project.is_archived && (
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">Архив</span>
              )}
            </div>

            {/* ГИПы */}
            <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-sm">
              {project.chief_engineer && (
                <span className="text-gray-600">
                  <span className="font-medium text-gray-800">ГИП:</span>{' '}
                  {project.chief_engineer.full_name}
                </span>
              )}
              {project.substitute_gip && (
                <span className="text-amber-700">
                  <span className="font-medium">Замещает:</span>{' '}
                  {project.substitute_gip.full_name}
                </span>
              )}
            </div>
            {project.address && (
              <p className="mt-0.5 text-sm text-gray-400">{project.address}</p>
            )}
          </div>

          {/* Кнопки действий */}
          <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
            <HelpButton />
            {canEdit && (
              <button
                onClick={() => setShowEditProject(true)}
                className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
              >
                <Pencil className="h-4 w-4" /> Изменить
              </button>
            )}
            {canTransfer && (
              <button
                onClick={() => setShowTransferGip(true)}
                className="flex items-center gap-1.5 rounded-lg border border-amber-300 px-3 py-2 text-sm text-amber-700 hover:bg-amber-50 transition-colors"
              >
                <UserCheck className="h-4 w-4" /> Передать ГИП
              </button>
            )}
            {canComplete && (
              <button
                onClick={() => setConfirmComplete(true)}
                className="flex items-center gap-1.5 rounded-lg border border-emerald-400 px-3 py-2 text-sm text-emerald-700 hover:bg-emerald-50 transition-colors"
              >
                <CheckCircle2 className="h-4 w-4" /> Завершить
              </button>
            )}
            {isDirectorFamily && !project.is_archived && (
              <button
                onClick={() => archiveMutation.mutate()}
                disabled={archiveMutation.isPending}
                className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-500 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <Archive className="h-4 w-4" /> В архив
              </button>
            )}
            {isDirectorFamily && project.is_archived && (
              <button
                onClick={() => unarchiveMutation.mutate()}
                disabled={unarchiveMutation.isPending}
                className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <ArchiveRestore className="h-4 w-4" /> Из архива
              </button>
            )}
            {isDirectorFamily && (
              <button
                onClick={() => setConfirmDeleteProject(true)}
                className="flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 transition-colors"
                data-tour="delete-project-btn"
              >
                <Trash2 className="h-4 w-4" /> В корзину
              </button>
            )}
          </div>
        </div>

        {/* ── Примечание проекта (ненавязчивая строка под шапкой) ── */}
        <ProjectNote
          note={project.note}
          canEdit={canEdit}
          onSave={(text) => noteMutation.mutateAsync(text)}
          isSaving={noteMutation.isPending}
        />

        {/* ── Диалог подтверждения завершения ── */}
        {confirmComplete && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-4">
            <CheckCircle2 className="h-6 w-6 text-emerald-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-emerald-900">Завершить проект?</p>
              <p className="text-xs text-emerald-700 mt-0.5">
                Проект перейдёт в стадию «Завершён» и появится в зоне «Завершённые» на дашборде.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => completeMutation.mutate()}
                disabled={completeMutation.isPending}
                className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {completeMutation.isPending ? 'Сохранение...' : 'Да, завершить'}
              </button>
              <button
                onClick={() => setConfirmComplete(false)}
                className="rounded-lg border border-emerald-300 px-3 py-1.5 text-sm text-emerald-700 hover:bg-emerald-100"
              >
                Отмена
              </button>
            </div>
          </div>
        )}

        {/* ── Мини-статистика ── */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'Разделов',   value: sStats.total,   cls: 'text-gray-700' },
            { label: 'Принято',    value: sStats.done,    cls: 'text-emerald-600' },
            { label: 'Просрочено', value: sStats.overdue, cls: sStats.overdue > 0 ? 'text-rose-600' : 'text-gray-400' },
            { label: 'Готовность', value: `${overallProgress}%`, cls: 'text-brand-600' },
          ].map(({ label, value, cls }) => (
            <div key={label} className="rounded-xl bg-white border border-gray-200 px-4 py-3 shadow-sm text-center">
              <div className={`text-2xl font-bold ${cls}`}>{value}</div>
              <div className="text-xs text-gray-500 mt-0.5">{label}</div>
            </div>
          ))}
        </div>

        {/* ── Сводка этапов ── */}
        {phases.length > 0 && (
          <PhasesSummary phases={phases} projectCost={project.cost} isDirectorFamily={isDirectorFamily} />
        )}

        {/* ── Диаграмма Ганта проекта (единое оформление с главным экраном) ── */}
        <GanttChart projectId={id!} title="Диаграмма Ганта · проект" />

        {/* ── Вкладки ── */}
        <div className="border-b border-gray-200" data-tour="project-tabs">
          <nav className="-mb-px flex gap-0 overflow-x-auto">
            {DOC_TYPES.map((dt) => (
              <button
                key={dt}
                onClick={() => setActiveTab(dt)}
                className={`px-5 pb-3 pt-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === dt
                    ? 'border-brand-500 text-brand-600'
                    : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300'
                }`}
              >
                {DOC_TYPE_LABELS[dt]}
              </button>
            ))}
            {isDirectorFamily && (
            <button
              data-tour="project-history-btn"
              onClick={() => setActiveTab('history')}
              className={`px-5 pb-3 pt-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'history'
                  ? 'border-brand-500 text-brand-600'
                  : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300'
              }`}
            >
              История
            </button>
            )}
          </nav>
        </div>

        {/* ── Диалог подтверждения удаления раздела ── */}
        {confirmDeleteSection && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 flex items-center gap-4">
            <Trash2 className="h-5 w-5 text-rose-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-rose-900">
                Удалить раздел <strong>{confirmDeleteSection.code}</strong>?
              </p>
              <p className="text-xs text-rose-700 mt-0.5">
                Все этапы раздела также будут удалены. Действие необратимо.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => deleteSectionMutation.mutate(confirmDeleteSection.id)}
                disabled={deleteSectionMutation.isPending}
                className="rounded-lg bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
              >
                {deleteSectionMutation.isPending ? 'Удаление...' : 'Удалить'}
              </button>
              <button
                onClick={() => setConfirmDeleteSection(null)}
                className="rounded-lg border border-rose-300 px-3 py-1.5 text-sm text-rose-700 hover:bg-rose-100"
              >
                Отмена
              </button>
            </div>
          </div>
        )}

        {stageCostWarning && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-700 flex items-start justify-between gap-3">
            <span>{stageCostWarning}</span>
            <button
              onClick={() => setStageCostWarning(null)}
              className="shrink-0 text-amber-500 hover:text-amber-700"
              aria-label="Закрыть"
            >
              ×
            </button>
          </div>
        )}

        {/* ── Вкладки: ИРД / ПД / РД / ГГЭ ── */}
        {activeTab !== 'history' && (
          <DocTypeTabContent
            projectId={id!}
            docType={activeTab as DocType}
            sections={sections}
            sectionsLoading={sLoading}
            canManage={canManage}
            canEdit={canEdit}
            isArchived={project.is_archived}
            onAddSection={() => setShowAddSection(true)}
            onEditSection={(s) => setEditSection(s)}
            onDeleteSection={isDirectorFamily ? (s) => setConfirmDeleteSection(s) : undefined}
            onStatusChange={(sid, status) => statusMutation.mutate({ sid, status })}
            effectiveRole={effectiveRole}
            userId={user?.id}
            userRole={user?.role}
          />
        )}

        {/* ── Вкладка: История ── */}
        {activeTab === 'history' && (
          <div className="rounded-xl bg-white border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900">История изменений</h2>
              <p className="text-xs text-gray-400 mt-0.5">Последние 100 событий по проекту и разделам</p>
            </div>
            {hLoading ? (
              <div className="p-8"><Spinner size="sm" /></div>
            ) : history.length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-gray-400">История пуста</div>
            ) : (
              <ul className="divide-y divide-gray-50">
                {history.map((entry) => <HistoryRow key={entry.id} entry={entry} />)}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* ── Модальные окна ── */}
      {showAddSection && id && (
        <AddSectionModal
          projectId={id}
          onClose={() => setShowAddSection(false)}
          docType={activeTab !== 'history' ? (activeTab as DocType) : undefined}
        />
      )}
      {editSection && id && (
        <EditSectionModal
          section={editSection}
          projectId={id}
          mode={canManage ? 'full' : 'progress'}
          showCost={isDirectorFamily}
          onClose={() => setEditSection(null)}
          onSaved={(w) => setStageCostWarning(w)}
        />
      )}
      {showEditProject && (
        <EditProjectModal project={project as ProjectOut} onClose={() => setShowEditProject(false)} />
      )}
      {showTransferGip && (
        <TransferGipModal project={project as ProjectOut} onClose={() => setShowTransferGip(false)} />
      )}

      {/* ── Модал подтверждения удаления проекта ── */}
      {confirmDeleteProject && (
        <>
          <div className="fixed inset-0 z-50 bg-black/40" onClick={() => setConfirmDeleteProject(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <div className="pointer-events-auto w-full max-w-sm rounded-xl bg-white shadow-2xl p-6">
              <h3 className="font-semibold text-gray-900 mb-2">Переместить проект в корзину?</h3>
              <p className="text-sm text-gray-600 mb-4">
                Проект будет скрыт из всех списков, но данные сохранятся.
                Директор сможет восстановить его на странице «Проекты → Удалённые».
              </p>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setConfirmDeleteProject(false)}
                  className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Отмена
                </button>
                <button
                  onClick={() => deleteProjectMutation.mutate()}
                  disabled={deleteProjectMutation.isPending}
                  className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50 transition-colors"
                >
                  {deleteProjectMutation.isPending ? 'Удаление...' : 'В корзину'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}

// ─── DocTypeTabContent ────────────────────────────────────────────────────────
function DocTypeTabContent({
  projectId: _projectId,
  docType: _docType,
  sections,
  sectionsLoading,
  canManage,
  canEdit,
  isArchived,
  onAddSection,
  onEditSection,
  onDeleteSection,
  onStatusChange,
  effectiveRole,
  userId,
  userRole,
}: {
  projectId: string
  docType: DocType
  sections: SectionOut[]
  sectionsLoading: boolean
  canManage: boolean
  canEdit: boolean
  isArchived: boolean
  onAddSection: () => void
  onEditSection: (s: SectionOut) => void
  onDeleteSection?: (s: SectionOut) => void
  onStatusChange: (sid: string, status: SectionStatus) => void
  effectiveRole: string | null
  userId: string | undefined
  userRole: string | undefined
}) {
  return (
    <div className="space-y-3">
      {sectionsLoading ? (
        <div className="rounded-xl bg-white border border-gray-200 shadow-sm p-8 flex justify-center">
          <Spinner size="sm" />
        </div>
      ) : (
        <SectionGanttView
          sections={sections}
          canManage={canManage}
          canEdit={canEdit}
          isArchived={isArchived}
          onAddSection={onAddSection}
          onEditSection={onEditSection}
          onDeleteSection={onDeleteSection}
          onStatusChange={onStatusChange}
          effectiveRole={effectiveRole}
          userId={userId}
          userRole={userRole}
        />
      )}
    </div>
  )
}

// ─── PhasesSummary ────────────────────────────────────────────────────────────
// DUE_SOON_DAYS: порог «скоро срок» — единый для всего приложения
const DUE_SOON_DAYS = 14

function PhasesSummary({
  phases,
  projectCost,
  isDirectorFamily,
}: {
  phases: DocPhaseOut[]
  projectCost?: string | null
  isDirectorFamily?: boolean
}) {
  const today = new Date()

  function fmt(d: string | null) {
    if (!d) return '-'
    try { return format(new Date(d), 'dd.MM.yyyy') } catch { return d }
  }

  function fmtCost(c: string | null) {
    if (!c) return '-'
    const n = parseFloat(c)
    return isNaN(n) ? c : n.toLocaleString('ru-RU') + ' ₽'
  }

  // Суммируем стоимости фаз и сравниваем с договорной стоимостью проекта
  const phasesSum = phases.reduce((acc, ph) => {
    const v = parseFloat(ph.cost ?? '0')
    return acc + (isNaN(v) ? 0 : v)
  }, 0)
  const totalCost = parseFloat(projectCost ?? '0')
  const hasTotalCost = !!projectCost && !isNaN(totalCost) && totalCost > 0
  const hasPhasesSum = phasesSum > 0
  const costsMatch = hasTotalCost && hasPhasesSum && Math.abs(phasesSum - totalCost) < 0.01

  return (
    <div className="space-y-3">
      {/* Блок договорной стоимости + сверки (только для директоров) */}
      {isDirectorFamily && (hasTotalCost || hasPhasesSum) && (
        <div className={`rounded-xl border px-4 py-3 shadow-sm flex flex-wrap items-center gap-3 ${
          !hasTotalCost
            ? 'bg-gray-50 border-gray-200'
            : costsMatch
              ? 'bg-emerald-50 border-emerald-300'
              : 'bg-amber-50 border-amber-300'
        }`}>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              {hasTotalCost && (
                <div>
                  <span className="text-xs text-gray-500">Договорная стоимость: </span>
                  <span className={`text-sm font-semibold ${costsMatch ? 'text-emerald-700' : 'text-gray-800'}`}>
                    {fmtCost(projectCost ?? null)}
                  </span>
                </div>
              )}
              {hasPhasesSum && (
                <div>
                  <span className="text-xs text-gray-500">Сумма по этапам: </span>
                  <span className={`text-sm font-semibold ${
                    costsMatch ? 'text-emerald-700' : hasTotalCost ? 'text-amber-700' : 'text-gray-800'
                  }`}>
                    {phasesSum.toLocaleString('ru-RU')} ₽
                  </span>
                </div>
              )}
            </div>
          </div>
          {hasTotalCost && hasPhasesSum && !costsMatch && (
            <div className="flex items-center gap-1.5 rounded-lg bg-amber-100 border border-amber-300 px-3 py-1.5">
              <span className="text-amber-600 text-base">⚠</span>
              <span className="text-xs font-medium text-amber-800">
                Суммы не совпадают - расхождение{' '}
                {Math.abs(phasesSum - totalCost).toLocaleString('ru-RU')} ₽
              </span>
            </div>
          )}
          {costsMatch && (
            <div className="flex items-center gap-1.5 rounded-lg bg-emerald-100 border border-emerald-300 px-3 py-1.5">
              <span className="text-emerald-600 text-base">✓</span>
              <span className="text-xs font-medium text-emerald-800">Стоимости совпадают</span>
            </div>
          )}
        </div>
      )}

      {/* Карточки этапов */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {DOC_TYPES.map((dt) => {
          const phase = phases.find((p) => p.doc_type === dt)
          const hasData = phase && (phase.start_date || phase.end_date || phase.cost)

          // Вычисляем состояние по дате окончания
          let urgency: 'overdue' | 'due_soon' | 'ok' = 'ok'
          let daysLeft: number | null = null
          if (phase?.end_date) {
            daysLeft = differenceInCalendarDays(new Date(phase.end_date), today)
            if (daysLeft < 0)          urgency = 'overdue'
            else if (daysLeft <= DUE_SOON_DAYS) urgency = 'due_soon'
          }

          const cardCls =
            urgency === 'overdue'  ? 'bg-rose-50 border-rose-300 shadow-rose-100'
            : urgency === 'due_soon' ? 'bg-amber-50 border-amber-300 shadow-amber-100'
            : hasData               ? 'bg-white border-gray-200'
            : 'bg-gray-50 border-gray-100'

          const titleCls =
            urgency === 'overdue'  ? 'text-rose-700'
            : urgency === 'due_soon' ? 'text-amber-700'
            : 'text-gray-700'

          return (
            <div key={dt} className={`rounded-xl border px-4 py-3 shadow-sm text-sm ${cardCls}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className={`font-semibold ${titleCls}`}>
                  {DOC_TYPE_LABELS[dt]}
                </span>
                {urgency === 'overdue' && daysLeft !== null && (
                  <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-700 whitespace-nowrap">
                    просрочка {Math.abs(daysLeft)} дн.
                  </span>
                )}
                {urgency === 'due_soon' && daysLeft !== null && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700 whitespace-nowrap">
                    {daysLeft === 0 ? 'сегодня' : `осталось ${daysLeft} дн.`}
                  </span>
                )}
              </div>
              <div className="space-y-0.5 text-xs text-gray-500">
                <div>
                  <span className="text-gray-400">Начало: </span>
                  <span className={phase?.start_date ? 'text-gray-700' : 'text-gray-300'}>
                    {fmt(phase?.start_date ?? null)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400">Окончание: </span>
                  <span className={
                    urgency === 'overdue'  ? 'text-rose-600 font-semibold'
                    : urgency === 'due_soon' ? 'text-amber-700 font-semibold'
                    : phase?.end_date       ? 'text-gray-700'
                    : 'text-gray-300'
                  }>
                    {fmt(phase?.end_date ?? null)}
                  </span>
                </div>
                {/* Стоимость этапа — только директорам */}
                {isDirectorFamily && (
                  <div className="pt-0.5 border-t border-gray-100 mt-1">
                    <span className="text-gray-400">Стоимость: </span>
                    <span className={phase?.cost ? 'text-gray-700 font-medium' : 'text-gray-300'}>
                      {fmtCost(phase?.cost ?? null)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── ProjectNote ──────────────────────────────────────────────────────────────
// Ненавязчивое примечание к проекту под шапкой карточки.
// Показывается только если примечание есть ИЛИ пользователь может редактировать.
// Правится инлайн, не мешает основной надписи (шифр — название).
function ProjectNote({
  note,
  canEdit,
  onSave,
  isSaving,
}: {
  note: string | null
  canEdit: boolean
  onSave: (text: string) => Promise<unknown>
  isSaving: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(note ?? '')

  // Черновик подхватывает внешние изменения (фоновый рефетч), пока не редактируем
  useEffect(() => {
    if (!editing) setDraft(note ?? '')
  }, [note, editing])

  async function handleSave() {
    try {
      await onSave(draft.trim())
      setEditing(false)
    } catch {
      /* ошибку показывать не будем — примечание не критично, останемся в режиме правки */
    }
  }

  if (editing) {
    return (
      <div className="space-y-1.5">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={2}
          autoFocus
          placeholder="Примечание к проекту"
          className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 resize-y"
        />
        <div className="flex gap-2">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="rounded-lg bg-brand-600 px-3 py-1 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {isSaving ? 'Сохранение…' : 'Сохранить'}
          </button>
          <button
            onClick={() => { setDraft(note ?? ''); setEditing(false) }}
            className="rounded-lg border border-gray-300 px-3 py-1 text-xs text-gray-600 hover:bg-gray-50"
          >
            Отмена
          </button>
        </div>
      </div>
    )
  }

  // Нет примечания и нельзя редактировать → не занимаем место
  if (!note && !canEdit) return null

  if (!note) {
    // Пусто, но можно добавить — деликатная подсказка
    return (
      <button
        onClick={() => setEditing(true)}
        className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-brand-600 transition-colors"
      >
        <StickyNote className="h-3.5 w-3.5" /> Добавить примечание
      </button>
    )
  }

  return (
    <div className="flex items-start gap-1.5 text-sm rounded-lg border-l-2 border-amber-200 bg-amber-50/40 px-3 py-1.5">
      <StickyNote className="h-3.5 w-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
      <p className="text-gray-500 whitespace-pre-wrap leading-snug flex-1 min-w-0">
        {note}
        {canEdit && (
          <button
            onClick={() => setEditing(true)}
            className="ml-2 align-middle text-xs text-gray-300 hover:text-brand-500"
          >
            изменить
          </button>
        )}
      </p>
    </div>
  )
}

// ─── SectionGanttView ────────────────────────────────────────────────────────
function statusBadgeCls(status: SectionStatus): string {
  switch (status) {
    case 'not_started': return 'bg-gray-100 text-gray-600'
    case 'in_progress': return 'bg-blue-100 text-blue-700'
    case 'on_review':   return 'bg-amber-100 text-amber-700'
    case 'suspended':   return 'bg-gray-100 text-gray-500'
    case 'issued':      return 'bg-emerald-100 text-emerald-700'
    case 'accepted':    return 'bg-emerald-200 text-emerald-800'
  }
}

/** Цвет бара: просрочка → rose, скоро срок → amber, иначе по статусу */
function sectionBarCls(status: SectionStatus, isOverdue: boolean, isDueSoon: boolean): string {
  if (isOverdue)  return 'bg-rose-500'
  if (isDueSoon)  return 'bg-amber-400'
  switch (status) {
    case 'not_started': return 'bg-gray-300'
    case 'in_progress': return 'bg-blue-400'
    case 'on_review':   return 'bg-amber-400'
    case 'suspended':   return 'bg-gray-300'
    case 'issued':      return 'bg-emerald-400'
    case 'accepted':    return 'bg-emerald-600'
  }
}

function SectionGanttView({
  sections,
  canManage,
  canEdit,
  isArchived,
  onAddSection,
  onEditSection,
  onDeleteSection,
  onStatusChange,
  effectiveRole,
  userId,
  userRole,
}: {
  sections: SectionOut[]
  canManage: boolean
  canEdit: boolean
  isArchived: boolean
  onAddSection: () => void
  onEditSection: (s: SectionOut) => void
  onDeleteSection?: (s: SectionOut) => void
  onStatusChange: (sid: string, status: SectionStatus) => void
  effectiveRole: string | null
  userId: string | undefined
  userRole: string | undefined
}) {
  const today = new Date()

  // ── ACL: стоимости видят только директор/замдир ───────────────────────────
  const showCost = effectiveRole === 'director' || effectiveRole === 'deputy_director'

  // ── Раскрытие этапов разделов ─────────────────────────────────────────────
  const [expandedStages, setExpandedStages] = useState<Set<string>>(new Set())
  const toggleStages = (sid: string) => {
    setExpandedStages(prev => {
      const n = new Set(prev)
      n.has(sid) ? n.delete(sid) : n.add(sid)
      return n
    })
  }

  // ── Диапазон дат для шкалы ───────────────────────────────────────────────
  let rangeStart: Date, rangeEnd: Date
  if (sections.length > 0) {
    const starts = sections.map((s) => new Date(s.planned_start).getTime())
    const ends   = sections.map((s) => new Date(s.planned_end).getTime())
    const minDate = new Date(Math.min(...starts))
    const maxDate = new Date(Math.max(...ends))
    rangeStart = new Date(minDate.getFullYear(), minDate.getMonth(), 1)
    rangeEnd   = new Date(maxDate.getFullYear(), maxDate.getMonth() + 1, 0)
  } else {
    rangeStart = new Date(today.getFullYear(), today.getMonth() - 1, 1)
    rangeEnd   = new Date(today.getFullYear(), today.getMonth() + 5, 0)
  }

  const totalMs = Math.max(rangeEnd.getTime() - rangeStart.getTime(), 1)

  function pct(d: Date | string): number {
    return Math.max(0, Math.min(100, (new Date(d).getTime() - rangeStart.getTime()) / totalMs * 100))
  }

  const todayPct  = pct(today)
  const showToday = todayPct >= 0 && todayPct <= 100

  // Метки месяцев
  const months: { label: string; left: number }[] = []
  let cur = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), 1)
  while (cur.getTime() < rangeEnd.getTime()) {
    months.push({ label: format(cur, 'MMM yy', { locale: ru }), left: pct(cur) })
    cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1)
  }

  // Минимальная ширина колонки Ганта: 80px на месяц → нет overlap меток + горизонтальный скролл
  const ganttMinWidth = Math.max(600, months.length * 80)

  function fmtD(d: string) {
    try { return format(new Date(d), 'dd.MM.yy') } catch { return d }
  }
  function fmtCost(c: string | null) {
    if (!c) return null
    const n = parseFloat(c)
    return isNaN(n) ? c : n.toLocaleString('ru-RU') + ' ₽'
  }

  return (
    <div className="rounded-xl bg-white border border-gray-200 shadow-sm overflow-hidden">
      {/* Шапка панели */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
        <h2 className="font-semibold text-gray-900 text-sm">
          Разделы
          {sections.length > 0 && (
            <span className="ml-2 font-normal text-gray-400">({sections.length})</span>
          )}
        </h2>
        {canManage && !isArchived && (
          <button
            data-tour="add-section-btn"
            onClick={onAddSection}
            className="flex items-center gap-1 text-sm text-brand-600 hover:text-brand-700 font-medium"
          >
            <Plus className="h-4 w-4" /> Добавить раздел
          </button>
        )}
      </div>

      {sections.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-gray-400">
          {canManage
            ? 'Разделы не добавлены. Нажмите «Добавить раздел» чтобы начать.'
            : 'Разделы не добавлены'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse" style={{ minWidth: '1000px' }}>
            <colgroup>
              <col style={{ width: '280px', minWidth: '280px' }} />
              <col />
              <col style={{ width: '210px', minWidth: '210px' }} />
            </colgroup>

            {/* Шапка с временно́й шкалой */}
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                  Раздел / Исполнитель
                </th>
                <th className="px-3 py-2">
                  {/* minWidth гарантирует ≥80px на месяц → текст не перекрывается */}
                  <div className="relative h-5" style={{ minWidth: ganttMinWidth }}>
                    {months.map((m, mi) => {
                      const nextLeft = mi + 1 < months.length ? months[mi + 1].left : 100
                      const cellW = (nextLeft - m.left) / 100 * ganttMinWidth
                      return (
                        <div
                          key={m.label}
                          className="absolute overflow-hidden"
                          style={{ left: `${m.left}%`, width: cellW, top: 0, bottom: 0 }}
                        >
                          <span className="text-xs text-gray-400 select-none whitespace-nowrap pl-1">
                            {m.label}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                  Готовность / Статус
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-50">
              {sections.map((s) => {
                const isActive = s.status !== 'accepted' && s.status !== 'suspended'
                const daysLeft = differenceInCalendarDays(new Date(s.planned_end), today)
                const isOverdue = daysLeft < 0 && isActive
                const isDueSoon = !isOverdue && daysLeft >= 0 && daysLeft <= DUE_SOON_DAYS && isActive

                const progress: number | null =
                  s.status === 'accepted' ? 100 : s.manual_progress ?? null

                const transitions   = SECTION_TRANSITIONS[s.status] ?? []
                // Директор и зам статус не меняют, только смотрят (запрос ГГП, октябрь 2026):
                // раздел ведёт исполнитель, принимает и возвращает ГИП проекта.
                // ГИП-исполнитель в чужом проекте меняет статус только своего раздела.
                const canChangeStat =
                  (userRole === 'chief_engineer' && canManage) ||
                  ((userRole === 'designer' || userRole === 'chief_engineer') && s.assignee_id === userId)

                const left  = pct(s.planned_start)
                const right = pct(s.planned_end)
                const width = Math.max(right - left, 0.5)
                const cost  = fmtCost(s.cost)

                const rowCls =
                  s.status === 'accepted' ? 'bg-emerald-50/40'
                  : isOverdue             ? 'bg-rose-50'
                  : isDueSoon             ? 'bg-amber-50/40'
                  : ''

                const hasStages = s.stages.length > 0
                const stagesOpen = expandedStages.has(s.id)

                return (
                  <Fragment key={s.id}>
                    {/* ── Строка раздела ── */}
                    <tr className={`hover:bg-gray-50/60 transition-colors ${rowCls}`}>

                      {/* ── Информация о разделе ── */}
                      <td className="px-4 py-2.5 align-top">
                        <div className="space-y-0.5">
                          {/* Шифр + code_name + кнопка этапов */}
                          <div className="flex items-baseline gap-1.5 flex-wrap">
                            <span className="font-semibold text-gray-900">{s.code}</span>
                            {hasStages && (
                              <button
                                onClick={() => toggleStages(s.id)}
                                className="inline-flex items-center gap-0.5 text-[10px] text-indigo-400 hover:text-indigo-600 select-none flex-shrink-0"
                                title={stagesOpen ? 'Свернуть этапы' : 'Показать этапы'}
                              >
                                {stagesOpen ? '▼' : '▶'}
                                <span>Этапы ({s.stages.length})</span>
                              </button>
                            )}
                            {s.code_name && (
                              <span
                                className="text-xs text-gray-400 truncate max-w-[140px]"
                                title={s.code_name}
                              >
                                {s.code_name}
                              </span>
                            )}
                            {s.note && (
                              <span className="text-gray-400 text-xs" title={s.note}>💬</span>
                            )}
                          </div>
                          {/* Наименование подраздела */}
                          {s.name && (
                            <div className="text-xs text-gray-700 leading-snug">{s.name}</div>
                          )}
                          {/* Пояснение */}
                          {s.description && (
                            <div
                              className="text-xs text-gray-400 italic leading-snug truncate"
                              title={s.description}
                            >
                              {s.description}
                            </div>
                          )}
                          {/* Даты · Исполнитель · Стоимость */}
                          <div className="flex flex-wrap items-center gap-x-1.5 text-xs text-gray-500 pt-0.5">
                            <span className={isOverdue ? 'text-rose-600 font-semibold' : isDueSoon ? 'text-amber-700 font-medium' : ''}>
                              {fmtD(s.planned_start)} - {fmtD(s.planned_end)}
                            </span>
                            {s.assignee?.full_name && (
                              <>
                                <span className="text-gray-300">·</span>
                                <span className="text-gray-600">{s.assignee.full_name}</span>
                              </>
                            )}
                            {showCost && cost && (
                              <>
                                <span className="text-gray-300">·</span>
                                <span className="text-gray-500">{cost}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* ── Диаграмма Ганта ── */}
                      <td className="px-3 py-2.5 align-middle">
                        <div
                          className="relative h-8 bg-gray-50 rounded-lg overflow-hidden"
                          style={{ minWidth: ganttMinWidth }}
                        >
                          {/* Сетка месяцев */}
                          {months.map((m) => (
                            <div
                              key={m.label}
                              className="absolute top-0 bottom-0 w-px bg-gray-200"
                              style={{ left: `${m.left}%` }}
                            />
                          ))}
                          {/* Линия «сегодня» */}
                          {showToday && (
                            <div
                              className="absolute top-0 bottom-0 w-0.5 bg-rose-400 z-10"
                              style={{ left: `${todayPct}%` }}
                            />
                          )}
                          {/* Бар раздела */}
                          <div
                            className={`absolute inset-y-1.5 rounded-md ${sectionBarCls(s.status, isOverdue, isDueSoon)}`}
                            style={{ left: `${left}%`, width: `${width}%` }}
                            title={`${fmtD(s.planned_start)} - ${fmtD(s.planned_end)}`}
                          />
                          {/* Фактическая выдача */}
                          {s.actual_end && (
                            <div
                              className="absolute top-0 bottom-0 w-1 bg-emerald-600 z-20 rounded-sm"
                              style={{ left: `${pct(s.actual_end)}%` }}
                              title={`Факт: ${fmtD(s.actual_end)}`}
                            />
                          )}
                        </div>
                      </td>

                      {/* ── Готовность / Статус ── */}
                      <td className="px-4 py-2.5 align-middle">
                        <div className="space-y-1.5">
                          {/* Метка просрочки / скорого срока */}
                          {isOverdue && (
                            <div className="flex items-center gap-1">
                              <span className="inline-block h-1.5 w-1.5 rounded-full bg-rose-500 flex-shrink-0" />
                              <span className="text-xs font-semibold text-rose-600 whitespace-nowrap">
                                просрочка {Math.abs(daysLeft)} дн.
                              </span>
                            </div>
                          )}
                          {isDueSoon && (
                            <div className="flex items-center gap-1">
                              <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                              <span className="text-xs font-semibold text-amber-700 whitespace-nowrap">
                                {daysLeft === 0 ? 'срок сегодня' : `осталось ${daysLeft} дн.`}
                              </span>
                            </div>
                          )}
                          {/* Прогресс-бар */}
                          {progress !== null ? (
                            <div className="flex items-center gap-1.5">
                              <div className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${isOverdue ? 'bg-rose-400' : progressColor(progress)}`}
                                  style={{ width: `${progress}%` }}
                                />
                              </div>
                              <span className={`text-xs font-medium tabular-nums w-8 text-right ${isOverdue ? 'text-rose-600' : 'text-gray-600'}`}>
                                {progress}%
                              </span>
                            </div>
                          ) : (
                            <div className="text-xs text-gray-300"> - %</div>
                          )}
                          {/* Статус + кнопки */}
                          <div className="flex items-center gap-1 flex-wrap">
                            {canChangeStat && transitions.length > 0 ? (
                              <select
                                value={s.status}
                                onChange={(e) => onStatusChange(s.id, e.target.value as SectionStatus)}
                                className="flex-1 min-w-0 rounded border border-gray-200 px-1.5 py-0.5 text-xs text-gray-700 bg-white focus:border-brand-400 focus:outline-none"
                              >
                                <option value={s.status}>{STATUS_LABELS[s.status]}</option>
                                {transitions.map((t) => (
                                  <option key={t.to} value={t.to}>{t.label}</option>
                                ))}
                              </select>
                            ) : (
                              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeCls(s.status)}`}>
                                {STATUS_LABELS[s.status]}
                              </span>
                            )}
                            {(canEdit ||
                              (userRole === 'designer' && s.assignee_id === userId && s.status === 'in_progress' && !isArchived)) && (
                              <button
                                onClick={() => onEditSection(s)}
                                className="rounded p-0.5 text-gray-400 hover:text-brand-600 hover:bg-gray-100 transition-colors flex-shrink-0"
                                title={canEdit ? 'Редактировать раздел' : 'Изменить % готовности'}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                            )}
                            {onDeleteSection && !isArchived && (
                              <button
                                onClick={() => onDeleteSection(s)}
                                className="rounded p-0.5 text-gray-300 hover:text-rose-500 hover:bg-rose-50 transition-colors flex-shrink-0"
                                title="Удалить раздел"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>

                    {/* ── Строки этапов раздела (раскрываются по клику) ── */}
                    {hasStages && stagesOpen && s.stages.map((stage) => {
                      const stLeft  = stage.start_date ? pct(stage.start_date) : null
                      const stRight = stage.end_date   ? pct(stage.end_date)   : null
                      const stWidth = stLeft !== null && stRight !== null ? Math.max(stRight - stLeft, 0.5) : 0
                      return (
                        <tr key={stage.id} className="bg-indigo-50/30 border-t border-indigo-100/50">
                          <td className="pl-8 pr-4 py-1.5 align-top">
                            <div className="flex items-center gap-1 flex-wrap">
                              <span className="text-[10px] text-gray-300 flex-shrink-0">└</span>
                              <span className="text-xs font-medium text-indigo-700">{stage.name}</span>
                            </div>
                            {(stage.start_date || stage.end_date) && (
                              <div className="text-[10px] text-gray-400 pl-3.5 pt-0.5">
                                {stage.start_date ? fmtD(stage.start_date) : '-'} - {stage.end_date ? fmtD(stage.end_date) : '-'}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-1.5 align-middle">
                            <div
                              className="relative h-5 bg-gray-100 rounded overflow-hidden"
                              style={{ minWidth: ganttMinWidth }}
                            >
                              {months.map((m) => (
                                <div
                                  key={m.label}
                                  className="absolute top-0 bottom-0 w-px bg-gray-200"
                                  style={{ left: `${m.left}%` }}
                                />
                              ))}
                              {showToday && (
                                <div
                                  className="absolute top-0 bottom-0 w-0.5 bg-rose-300 z-10"
                                  style={{ left: `${todayPct}%` }}
                                />
                              )}
                              {stLeft !== null && stWidth > 0 && (
                                <div
                                  className="absolute inset-y-0.5 rounded bg-indigo-300"
                                  style={{ left: `${stLeft}%`, width: `${stWidth}%` }}
                                  title={`${stage.start_date ? fmtD(stage.start_date) : '-'} - ${stage.end_date ? fmtD(stage.end_date) : '-'}`}
                                />
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-1.5 align-middle">
                            {showCost && stage.cost && (
                              <span className="text-xs text-indigo-600 font-medium tabular-nums">
                                {fmtCost(stage.cost)}
                              </span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ─── HistoryRow ───────────────────────────────────────────────────────────────
function HistoryRow({ entry }: { entry: AuditEntry }) {
  const entityLabel = entry.entity_type === 'project' ? 'Проект' : 'Раздел'
  const actionColor: Record<string, string> = {
    create:        'bg-emerald-100 text-emerald-700',
    update:        'bg-blue-100 text-blue-700',
    delete:        'bg-rose-100 text-rose-700',
    status_change: 'bg-amber-100 text-amber-700',
  }

  const isChange = entry.action === 'update' || entry.action === 'status_change'

  return (
    <li className="px-5 py-3 flex items-start gap-3">
      <span className={`flex-shrink-0 mt-0.5 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${actionColor[entry.action] ?? 'bg-gray-100 text-gray-600'}`}>
        {entry.action_label}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="text-xs text-gray-500 font-medium">{entityLabel}</span>
          <span className="text-xs text-gray-400 ml-auto flex-shrink-0">
            {format(new Date(entry.created_at), 'd MMM yyyy HH:mm', { locale: ru })}
          </span>
        </div>
        {isChange && entry.changes && entry.changes.length > 0 && (
          <div className="mt-0.5 space-y-0 leading-tight">
            {entry.changes.map((c) => (
              <p key={c.field} className="text-sm text-gray-700">
                <span className="text-gray-500">{c.field_label}:</span>{' '}
                <span className="text-rose-600 line-through decoration-rose-300">{c.old}</span>
                {' → '}
                <span className="text-emerald-700 font-medium">{c.new}</span>
              </p>
            ))}
          </div>
        )}
      </div>
    </li>
  )
}
